import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { CreditTransaction, OrderStats, ReportOrder } from "../types";
import type { FullReportRealEstateRow } from "../lib/report-data";

// Keep in sync with REPORT_SELECT in supabase/functions/_shared/report-select.ts
// and the column list in public.get_report_view() — the shared-report edge
// function and the RPC must both return this same shape.
export const FULL_REPORT_SELECT = [
  "id",
  "address",
  "address_num",
  "address_num_suffix",
  "address_zip",
  "address_city",
  "lat",
  "lon",
  "building_type",
  "building",
  "building_parts",
  "building_part",
  "building_land",
  "ai_overview",
  "ai_valuation",
  "ai_location",
  "ai_location_poi",
  "ai_cadaster",
  "ai_key_features",
  "ai_summary",
  "valuation",
  "valuation_land",
  "valuation_pdf",
  "market_analysis",
  "similar_ads",
  "similar_etn_deals",
  "etn_trends_chart_url",
  "energy_pdf_id",
].join(", ");

// Explicit columns instead of select("*"): report_orders is wide (llm_usage
// alone is over half a row) and the dashboard needs a fraction of it. Must list
// exactly the fields of ReportOrder / CreditTransaction in ../types.
const REPORT_ORDER_COLUMNS =
  "id,user_id,property_address,property_type,apartment_number,status,generation_error,attachment_url,pdf_generated_url,thumbnail_url,real_estate_id,webhook_response,is_preview,unlocked_at,unlock_requested_at,share_token,created_at,updated_at";

const CREDIT_TRANSACTION_COLUMNS =
  "id,credits_change,transaction_type,description,expires_at,created_at";

export async function fetchCreditBalance(userId: string): Promise<number> {
  const { data, error } = await supabase.rpc("get_valid_credit_balance", {
    user_uuid: userId,
  });

  if (error) throw error;
  return data ?? 0;
}

/** Credits the user actually paid for — the free bonus doesn't unlock a preview. */
export async function fetchPaidCreditBalance(userId: string): Promise<number> {
  const { data, error } = await supabase.rpc("get_paid_credit_balance", {
    user_uuid: userId,
  });

  if (error) throw error;
  return data ?? 0;
}

export const REPORT_ORDERS_PAGE_SIZE = 20;

export async function fetchReportOrders(userId: string): Promise<ReportOrder[]> {
  const { data, error } = await supabase
    .from("report_orders")
    .select(REPORT_ORDER_COLUMNS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .limit(REPORT_ORDERS_PAGE_SIZE);

  if (error) throw error;
  return data ?? [];
}

/** Keep older active orders visible without downloading completed history. */
export async function fetchActiveReportOrders(userId: string): Promise<ReportOrder[]> {
  const { data, error } = await supabase
    .from("report_orders")
    .select(REPORT_ORDER_COLUMNS)
    .eq("user_id", userId)
    .or("status.ilike.pending,status.ilike.processing")
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .limit(REPORT_ORDERS_PAGE_SIZE);
  if (error) throw error;
  return data ?? [];
}

export type ReportOrdersCursor = Pick<ReportOrder, "created_at" | "id">;
export type ReportOrdersPage = {
  orders: ReportOrder[];
  nextCursor: ReportOrdersCursor | null;
};

/** A stable cursor avoids shifting older pages when new orders arrive. */
export async function fetchReportOrdersPage(
  userId: string,
  cursor: ReportOrdersCursor | null = null,
): Promise<ReportOrdersPage> {
  let query = supabase
    .from("report_orders")
    .select(REPORT_ORDER_COLUMNS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false, nullsFirst: false })
    .order("id", { ascending: false })
    .limit(REPORT_ORDERS_PAGE_SIZE + 1);

  if (cursor) {
    // Date-less legacy orders sort last, then by id like every other page.
    query = cursor.created_at === null
      ? query.is("created_at", null).lt("id", cursor.id)
      : query.or(`created_at.lt."${cursor.created_at}",and(created_at.eq."${cursor.created_at}",id.lt.${cursor.id}),created_at.is.null`);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = data ?? [];
  const orders = rows.slice(0, REPORT_ORDERS_PAGE_SIZE);
  const last = orders.at(-1);
  return {
    orders,
    nextCursor: rows.length > REPORT_ORDERS_PAGE_SIZE && last
      ? { created_at: last.created_at, id: last.id }
      : null,
  };
}

/** Count in the database; HEAD responses contain no report rows. */
export async function fetchOrderStats(userId: string): Promise<OrderStats> {
  const countQuery = () => supabase
    .from("report_orders")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId);

  const [total, completed, inProgress] = await Promise.all([
    countQuery(),
    countQuery().ilike("status", "completed"),
    countQuery().or("status.ilike.pending,status.ilike.processing"),
  ]);
  for (const result of [total, completed, inProgress]) {
    if (result.error) throw result.error;
  }
  return {
    total: total.count ?? 0,
    completed: completed.count ?? 0,
    inProgress: inProgress.count ?? 0,
  };
}

export async function fetchReportOrder(
  userId: string,
  orderId: string,
): Promise<ReportOrder | null> {
  const { data, error } = await supabase
    .from("report_orders")
    .select(REPORT_ORDER_COLUMNS)
    .eq("id", orderId)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Report data for one order, through the gating RPC.
 *
 * Reports are NOT read from `real_estate` directly: a locked preview must never
 * have its analysis reach the browser, and a client-side filter would be theatre
 * — the row would still be in the network response. get_report_view() checks
 * ownership and lock state in the database and redacts there, returning the same
 * column shape either way (locked fields come back null).
 */
export async function fetchReportView(
  orderId: string,
): Promise<FullReportRealEstateRow | null> {
  const { data, error } = await supabase.rpc("get_report_view", {
    p_order_id: orderId,
  });

  if (error) throw error;
  return toReportViewRow(data);
}

/**
 * get_report_view() returns jsonb, so the generated types only say `Json`. Its
 * contract is one object with the FULL_REPORT_SELECT columns (locked fields
 * null) or null; anything else is treated as "no report".
 */
function toReportViewRow(data: Json | null): FullReportRealEstateRow | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  return data as Record<string, unknown> as FullReportRealEstateRow;
}

export async function fetchCreditTransactions(
  userId: string,
): Promise<CreditTransaction[]> {
  const { data, error } = await supabase
    .from("credit_transactions")
    .select(CREDIT_TRANSACTION_COLUMNS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return data ?? [];
}

export type GenerationJobRow = {
  id: string;
  order_id: string;
  status: string;
  current_step: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Latest generation job per order, for the live progress tracker. Owners may
 * read their own jobs (RLS, migration 20261008130000); the row is narrow on
 * purpose — the customer needs the phase, not the metadata.
 */
export async function fetchGenerationJobs(orderIds: string[]): Promise<Record<string, GenerationJobRow>> {
  if (orderIds.length === 0) return {};
  const { data, error } = await supabase
    .from("report_generation_jobs")
    .select("id,order_id,status,current_step,created_at,updated_at")
    .in("order_id", orderIds)
    .order("created_at", { ascending: false });

  if (error) throw error;
  const latest: Record<string, GenerationJobRow> = {};
  for (const row of data ?? []) {
    if (!latest[row.order_id]) latest[row.order_id] = row;
  }
  return latest;
}
